/**
 * Myoform Guided Quiz — flow controller.
 *
 * Responsibilities:
 *   - read the config emitted by snippets/quiz-config.liquid
 *   - hold selection state and persist it across refreshes
 *   - resolve the visible step path (the plan step is skipped when the user
 *     declines supplements)
 *   - compute the order summary from state on every render
 *   - build the Shopify cart and hand off to /checkout
 *
 * Presentation lives in myoform-quiz-components.js; content lives in the
 * config. Neither is edited to change the other.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-mqz-root]');
  if (!root || root.dataset.mqzInit === '1') return;

  var C = window.MyoQuizComponents;
  if (!C) {
    console.error('[MyoQuiz] Components not loaded.');
    return;
  }

  var configEl = document.getElementById('MyoQuizConfig');
  if (!configEl) {
    console.error('[MyoQuiz] Config not found.');
    return;
  }

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (err) {
    console.error('[MyoQuiz] Config is not valid JSON.', err);
    return;
  }

  root.dataset.mqzInit = '1';

  document.documentElement.classList.add('mqz-page-locked');
  document.body.classList.add('mqz-page-locked');

  var SETTINGS = config.settings || {};
  var LABELS = SETTINGS.labels || {};
  var PRODUCTS = config.products || {};
  var STEPS = config.steps || [];

  var stageEl = root.querySelector('[data-mqz-stage]');
  var progressEl = root.querySelector('[data-mqz-progress]');
  var stickyEl = root.querySelector('[data-mqz-sticky-mount]');
  var faqEl = root.querySelector('[data-mqz-faq]');

  /* ======================================================================
     State
     ====================================================================== */

  var state = {
    stepId: STEPS.length ? STEPS[0].id : null,
    dnaChoice: null,
    selectedGoal: null,
    supplementChoice: null,
    membershipPlan: null,
    upsells: [],
    upsellDismissed: false,
    upgradeDismissed: false,
    /* 'pro' when the user arrived from a Myoform Pro CTA on the pricing page. */
    tier: null,
    /* Steps the user has actually visited, so Back retraces the real path
       rather than guessing at the projected one. */
    history: []
  };

  restore();
  applyUrlParams();

  /* ======================================================================
     Persistence
     ====================================================================== */

  function storageKey() {
    return SETTINGS.storageKey || 'myoform_quiz_v1';
  }

  function persist() {
    if (!SETTINGS.persist) return;
    try {
      sessionStorage.setItem(
        storageKey(),
        JSON.stringify({
          savedAt: Date.now(),
          state: {
            stepId: state.stepId,
            dnaChoice: state.dnaChoice,
            selectedGoal: state.selectedGoal,
            supplementChoice: state.supplementChoice,
            membershipPlan: state.membershipPlan,
            upsells: state.upsells,
            upsellDismissed: state.upsellDismissed,
            upgradeDismissed: state.upgradeDismissed,
            tier: state.tier,
            history: state.history
          }
        })
      );
    } catch (err) {
      /* Private browsing or a full quota — the flow still works in memory. */
    }
  }

  function restore() {
    if (!SETTINGS.persist) return;
    try {
      var raw = sessionStorage.getItem(storageKey());
      if (!raw) return;

      var saved = JSON.parse(raw);
      var ttl = (SETTINGS.storageTtlMinutes || 120) * 60 * 1000;
      if (!saved || !saved.state || Date.now() - (saved.savedAt || 0) > ttl) {
        sessionStorage.removeItem(storageKey());
        return;
      }

      /* Migrate legacy multi-select goals sessions. */
      if (saved.state.selectedGoals && !saved.state.selectedGoal) {
        var legacyGoals = saved.state.selectedGoals;
        if (Array.isArray(legacyGoals) && legacyGoals.length) {
          saved.state.selectedGoal = legacyGoals[0];
        }
      }

      Object.keys(saved.state).forEach(function (key) {
        if (key === 'selectedGoals') return;
        if (saved.state[key] !== undefined) state[key] = saved.state[key];
      });

      /* Guard against a config change that removed the persisted step. */
      if (!findStep(state.stepId)) state.stepId = STEPS.length ? STEPS[0].id : null;
    } catch (err) {
      /* Ignore unreadable sessions and start fresh. */
    }
  }

  /**
   * Preselects answers handed over by the pricing page
   * (?plan=pro|premium&billing=quarterly|monthly|onetime&dna=kit|upload).
   * The user still walks every step; only a billing with a matching plan
   * option here is preselected.
   */
  function applyUrlParams() {
    var params = new URLSearchParams(window.location.search);
    var dna = params.get('dna');
    var billing = params.get('billing');
    var billingToPlan = { monthly: 'monthly', onetime: 'threeMonth' };

    if ((dna === 'kit' || dna === 'upload') && findOption('dna', dna)) state.dnaChoice = dna;
    if (params.get('plan')) {
      state.supplementChoice = 'yes';
      state.tier = params.get('plan') === 'pro' ? 'pro' : null;
    }
    if (billingToPlan[billing] && findOption('plan', billingToPlan[billing])) {
      state.membershipPlan = billingToPlan[billing];
    }
  }

  function clearPersisted() {
    try {
      sessionStorage.removeItem(storageKey());
    } catch (err) {
      /* No-op. */
    }
  }

  /* ======================================================================
     Formatting
     ====================================================================== */

  var SYMBOL = SETTINGS.currencySymbol || '£';

  function formatMoney(amount) {
    if (amount === null || amount === undefined || isNaN(amount)) return '';
    var locale = SETTINGS.currency === 'USD' ? 'en-US' : 'en-GB';
    var hasPence = Math.abs(amount % 1) > 0.001;

    return (
      SYMBOL +
      Number(amount).toLocaleString(locale, {
        minimumFractionDigits: hasPence ? 2 : 0,
        maximumFractionDigits: hasPence ? 2 : 0
      })
    );
  }

  /**
   * Replaces {cur}, {amount}, {code} and any extra tokens in config strings,
   * so copy in quiz-config.liquid stays currency- and price-agnostic.
   */
  function interpolate(template, tokens) {
    if (!template) return '';
    var map = Object.assign(
      { cur: SYMBOL, code: SETTINGS.discountCode || '' },
      tokens || {}
    );

    return String(template).replace(/\{(\w+)\}/g, function (match, key) {
      if (!(key in map)) return match;
      var value = map[key];
      return key === 'amount' ? formatMoney(value).replace(SYMBOL, '') : String(value);
    });
  }

  /* ======================================================================
     Step + option lookup
     ====================================================================== */

  function findStep(id) {
    for (var i = 0; i < STEPS.length; i++) {
      if (STEPS[i].id === id) return STEPS[i];
    }
    return null;
  }

  function findOption(stepId, optionId) {
    var step = findStep(stepId);
    if (!step || !step.options) return null;
    for (var i = 0; i < step.options.length; i++) {
      if (step.options[i].id === optionId) return step.options[i];
    }
    return null;
  }

  function findPlan(planId) {
    var step = findStepByType('plan-select');
    if (!step) return null;
    for (var i = 0; i < (step.options || []).length; i++) {
      if (step.options[i].id === planId) return step.options[i];
    }
    return null;
  }

  function findStepByType(type) {
    for (var i = 0; i < STEPS.length; i++) {
      if (STEPS[i].type === type) return STEPS[i];
    }
    return null;
  }

  /**
   * A step is in the path only when its showIf conditions hold. This is what
   * makes the plan step disappear for "no supplements" without any bespoke
   * branching in the render code.
   */
  function stepIsVisible(step) {
    if (!step.showIf) return true;
    return Object.keys(step.showIf).every(function (key) {
      return state[key] === step.showIf[key];
    });
  }

  /** Steps whose conditions are satisfied right now. Drives navigation. */
  function visibleSteps() {
    return STEPS.filter(stepIsVisible);
  }

  /**
   * Steps counted by the progress indicator. A conditional step whose
   * deciding answer has not been given yet is counted optimistically, so the
   * flow opens at "1 of 5" rather than understating its own length and then
   * appearing to grow. Once the answer rules a step out it drops away.
   */
  function projectedSteps() {
    return STEPS.filter(function (step) {
      if (!step.showIf) return true;
      return Object.keys(step.showIf).every(function (key) {
        return state[key] === null || state[key] === undefined || state[key] === step.showIf[key];
      });
    });
  }

  /* ======================================================================
     Navigation
     ====================================================================== */

  function currentStep() {
    return findStep(state.stepId) || STEPS[0];
  }

  /**
   * Resolves the next step id. An option's own `next` wins, which is how the
   * config controls routing; otherwise it's the following visible step.
   */
  function nextStepId() {
    var step = currentStep();
    if (!step) return null;

    if (step.stateKey && step.type === 'single-select') {
      var chosen = findOption(step.id, state[step.stateKey]);
      if (chosen && chosen.next) return chosen.next;
    }
    if (step.next) return step.next;

    var path = visibleSteps();
    for (var i = 0; i < path.length; i++) {
      if (path[i].id === step.id) return path[i + 1] ? path[i + 1].id : null;
    }
    return null;
  }

  function goTo(stepId, options) {
    var opts = options || {};
    var target = findStep(stepId);
    if (!target) return;

    if (opts.pushHistory !== false && state.stepId && state.stepId !== stepId) {
      state.history.push(state.stepId);
    }

    state.stepId = stepId;
    render();
    persist();

    if (opts.scroll !== false) scrollIntoView();
  }

  function goNext() {
    if (!validateCurrent()) return;
    var next = nextStepId();
    if (next) goTo(next);
  }

  function goBack() {
    var previous = state.history.pop();
    if (!previous) return;

    /* If the config changed under a persisted session, fall back to the
       nearest visible step rather than dead-ending. */
    if (!stepIsVisible(findStep(previous) || {})) {
      var path = visibleSteps();
      previous = path.length ? path[0].id : previous;
    }

    state.stepId = previous;
    render();
    persist();
    scrollIntoView();
  }

  function scrollIntoView() {
    if (document.body.classList.contains('mqz-page-locked')) return;
    var top = root.getBoundingClientRect().top + window.pageYOffset - 24;
    window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
  }

  /* ======================================================================
     Validation
     ====================================================================== */

  function validateCurrent() {
    var step = currentStep();
    if (!step || !step.required) return true;

    if (step.type === 'multi-select') {
      var min = step.minSelections || 1;
      if ((state[step.stateKey] || []).length < min) {
        showError(LABELS.errorSelectGoal || 'Please select at least one option.');
        return false;
      }
      return true;
    }

    if (!state[step.stateKey]) {
      showError(LABELS.errorSelectOne || 'Please choose an option to continue.');
      return false;
    }
    return true;
  }

  function isStepComplete(step) {
    if (!step || !step.required) return true;
    if (step.type === 'multi-select') {
      return (state[step.stateKey] || []).length >= (step.minSelections || 1);
    }
    return Boolean(state[step.stateKey]);
  }

  function showError(message) {
    var el = root.querySelector('[data-mqz-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  function clearError() {
    var el = root.querySelector('[data-mqz-error]');
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
  }

  /* ======================================================================
     Order summary — derived from state on every render
     ====================================================================== */

  /**
   * The effective plan is whichever plan is selected, plus any plan added
   * from the summary upsell. Upsells never mutate membershipPlan directly so
   * the user's original answer stays recoverable.
   */
  function effectivePlanId() {
    if (state.supplementChoice === 'yes' && state.membershipPlan) return state.membershipPlan;
    var upsell = state.upsells.find(function (u) {
      return u.type === 'plan';
    });
    return upsell ? upsell.planId : null;
  }

  /**
   * The Welcome Kit is a £99 product that the store's bundle discount function
   * only zeroes for a membership. A plan that opts out must therefore neither
   * advertise it nor put it in the cart, or checkout charges for it.
   * Myoform Pro earns it on subscriptions only: the function zeroes it for a
   * formula line carrying the _tier: pro property and a selling plan.
   */
  function planIncludesWelcomeKit(plan) {
    if (!plan || !SETTINGS.includeWelcomeKitWithPlan) return false;
    if (state.tier === 'pro') return Boolean(plan.sellingPlanId);
    return plan.includesWelcomeKit !== false;
  }

  /**
   * The Genetics Expert Consultation is zeroed by the same bundle discount
   * function, and only for a monthly or annual subscription, so it carries the
   * same contract as the Welcome Kit above.
   */
  function planIncludesConsultation(plan) {
    if (!plan || !SETTINGS.includeConsultationWithPlan) return false;
    return plan.includesConsultation !== false;
  }

  /**
   * A plan whose config carries deferredBilling is quoted as charged after the
   * genetic results rather than at checkout, leaving the DNA line as the only
   * thing due today. It applies only while the DNA line still carries a charge:
   * on the upload path the plan makes that line free, so deferring the plan too
   * would put nothing at all against "Due today".
   */
  function deferredBilling(plan, dnaAmount) {
    var defer = plan && plan.deferredBilling;
    if (!defer) return null;
    if (defer.requiresDnaKit && state.dnaChoice !== 'kit') return null;
    if (!dnaAmount) return null;
    return defer;
  }

  function computeSummary() {
    var planId = effectivePlanId();
    var plan = planId ? findPlan(planId) : null;

    /* --- DNA line ------------------------------------------------------ */
    var dna = null;
    var dnaAmount = 0;

    if (state.dnaChoice) {
      var dnaOption = findOption('dna', state.dnaChoice);
      var isKit = state.dnaChoice === 'kit';
      var product = isKit ? PRODUCTS.dnaKit : PRODUCTS.dnaUpload;
      var listPrice = (dnaOption && dnaOption.price) || (product && product.price) || 0;

      dnaAmount = listPrice;
      var wasAmount = null;

      if (plan) {
        if (isKit && plan.dnaKitDiscount) {
          dnaAmount = Math.max(0, listPrice - plan.dnaKitDiscount);
          wasAmount = listPrice;
        } else if (!isKit && plan.dnaUploadFree) {
          dnaAmount = 0;
          wasAmount = listPrice;
        }
      }

      dna = {
        title: (product && product.label) || (dnaOption && dnaOption.title) || '',
        image: (dnaOption && dnaOption.image) || (product && product.image) || '',
        amount: dnaAmount,
        wasAmount: wasAmount
      };
    }

    /* --- Goals --------------------------------------------------------- */
    var goals = [];
    if (state.selectedGoal) {
      var goalOption = findOption('goals', state.selectedGoal);
      if (goalOption) goals.push(goalOption.title);
    }

    /* --- Plan line ----------------------------------------------------- */
    var planLine = null;
    var defer = deferredBilling(plan, dnaAmount);

    if (plan) {
      planLine = {
        title: (PRODUCTS.formula && PRODUCTS.formula.label) || 'Personalized Formula',
        planName: plan.name,
        image: (PRODUCTS.formula && PRODUCTS.formula.image) || '',
        amount: plan.priceDueToday,
        wasAmount: plan.priceCompareAt || null,
        /* Set only when the charge is deferred: the line then quotes the
           recurring price instead of a figure due today, and says when it
           is taken. */
        deferredAmount: defer ? interpolate(defer.amount, { amount: plan.priceDueToday }) : '',
        note: defer ? interpolate(defer.lineNote, { amount: plan.priceDueToday }) : ''
      };
    } else if (state.supplementChoice === 'no') {
      /* Still render the row so the decision stays visible. */
      planLine = { declined: true };
    }

    /* --- Free gifts ---------------------------------------------------- */
    /* One row per gift the selected plan earns, in the order they are listed
       here. Each is in the cart at full price and zeroed by the bundle
       discount function, so a plan that does not qualify must not list one. */
    var gifts = [];
    if (planIncludesWelcomeKit(plan) && PRODUCTS.welcomeKit) {
      gifts.push({
        title: PRODUCTS.welcomeKit.label || 'Welcome Kit',
        image: PRODUCTS.welcomeKit.image || '',
        wasAmount: PRODUCTS.welcomeKit.compareAtPrice || null
      });
    }
    if (planIncludesConsultation(plan) && PRODUCTS.consultation) {
      gifts.push({
        title: PRODUCTS.consultation.label || 'Genetics Expert Consultation',
        image: PRODUCTS.consultation.image || '',
        wasAmount: PRODUCTS.consultation.compareAtPrice || null
      });
    }

    /* --- Totals -------------------------------------------------------- */
    /* Only what is charged today; the sticky bar is where this surfaces now
       that the summary has no totals panel. A deferred plan is left out of it
       and carried by dueLater instead, so the kit stands alone. */
    var dueToday = dnaAmount + (plan && !defer ? plan.priceDueToday : 0);
    var dueLater = defer ? interpolate(defer.totalNote, { amount: plan.priceDueToday }) : '';

    return {
      plan: planLine,
      planConfig: plan,
      dna: dna,
      goals: goals,
      gifts: gifts,
      dueToday: dueToday,
      dueLater: dueLater
    };
  }

  /* ======================================================================
     Render
     ====================================================================== */

  function renderContext() {
    return {
      formatMoney: formatMoney,
      interpolate: interpolate,
      labels: LABELS,
      reassurance: SETTINGS.reassurance || [],
      trust: SETTINGS.trust || null
    };
  }

  function render() {
    var step = currentStep();
    if (!step) return;

    renderProgress(step);
    stageEl.innerHTML = renderStep(step);
    renderSticky(step);
    renderPageScroll(step);
    bindStage();
  }

  /**
   * Every question is sized to the locked viewport, but the summary carries the
   * FAQ below it, so that one step hands the scroll back to the document.
   */
  function renderPageScroll(step) {
    var scrolls = step.type === 'summary';

    document.documentElement.classList.toggle('mqz-page-scrolls', scrolls);
    document.body.classList.toggle('mqz-page-scrolls', scrolls);

    if (faqEl) faqEl.hidden = !scrolls;
    if (!scrolls) {
      window.scrollTo({ top: 0 });
      /* Coming back from the summary re-locks the page, and the window may
         have been resized while the lock was off. */
      measureHeaderOffset();
    }
  }

  function renderProgress(step) {
    var path = projectedSteps();
    var index = path.findIndex(function (s) {
      return s.id === step.id;
    });

    progressEl.innerHTML = C.QuizProgress({
      current: index >= 0 ? index + 1 : 1,
      total: path.length,
      canGoBack: state.history.length > 0,
      template: LABELS.progress,
      backLabel: LABELS.back
    });
  }

  function renderStep(step) {
    var ctx = renderContext();

    switch (step.type) {
      case 'single-select':
        return C.QuizLayout(
          step,
          renderSingleSelect(step, ctx),
          step.autoAdvance ? '' : C.StepFooter(step, ctx)
        );
      case 'multi-select':
        return C.QuizLayout(step, renderMultiSelect(step, ctx), C.StepFooter(step, ctx));
      case 'plan-select':
        return C.QuizLayout(step, renderPlanSelect(step, ctx), C.StepFooter(step, ctx));
      case 'interstitial':
        return C.QuizLayout(step, C.Interstitial(step), '');
      case 'summary':
        return C.QuizLayout(step, renderSummary(step, ctx), renderSummaryFooter());
      default:
        return C.QuizLayout(step, '', '');
    }
  }

  /** Picks the card component named by the step's cardStyle. */
  function cardFor(style) {
    if (style === 'product') return C.ProductCard;
    if (style === 'goal') return C.GoalCard;
    if (style === 'text') return C.TextChoiceCard;
    if (style === 'pricing') return C.PricingCard;
    return C.OptionCard;
  }

  /**
   * Maps a step's `layout` to its grid class. The stacked layouts are the
   * flow's default shape: one option per row, in a narrow column, sized to
   * stay inside the locked viewport.
   */
  function gridClass(layout) {
    if (layout === 'stack-goals') return 'stack-goals';
    if (layout === 'stack-cards') return 'stack-cards';
    if (layout === 'stack-plans') return 'stack-plans';
    if (layout === 'grid-goals') return 'grid-goals';
    if (layout === 'grid-three') return 'grid-three';
    if (layout === 'two-up') return 'two-up';
    return 'two-up';
  }

  function renderSingleSelect(step, ctx) {
    var Card = cardFor(step.cardStyle);
    var selected = state[step.stateKey];

    var cards = (step.options || [])
      .map(function (option) {
        return Card(option, Object.assign({}, ctx, { selected: selected === option.id }));
      })
      .join('');

    var grid =
      '<div class="mqz__grid mqz__grid--' +
      gridClass(step.layout) +
      '" role="radiogroup" aria-labelledby="mqz-headline-' +
      C.escape(step.id) +
      '">' +
      cards +
      '</div>';

    /* The split-hero layout pairs a product image with a stacked choice
       column, then a testimonial underneath (Step 3). */
    if (step.layout === 'split-hero') {
      return (
        '<div class="mqz__split">' +
        renderHero(step.hero) +
        '<div class="mqz__choices" role="radiogroup" aria-labelledby="mqz-headline-' +
        C.escape(step.id) +
        '">' +
        cards +
        '</div>' +
        '</div>' +
        (step.testimonial ? '<div class="mqz__extras">' + C.TestimonialCard(step.testimonial) + '</div>' : '')
      );
    }

    /* A note qualifying the options — on the DNA step, what a membership does
       to the prices on the cards. It shares a wrapper with the grid so it
       stays under the last card instead of dropping to the foot of the locked
       viewport, which is where the grid's own growth would leave it. */
    if (step.footnote) {
      return (
        '<div class="mqz__options">' + grid + C.OptionsNote(step.footnote, ctx) + '</div>'
      );
    }

    return grid;
  }

  function renderHero(hero) {
    if (!hero) return '<div></div>';

    var stats = '';
    if (hero.stats && hero.stats.length) {
      stats =
        '<div class="mqz-hero__stats">' +
        hero.stats
          .map(function (stat) {
            return (
              '<div class="mqz-hero__stat">' +
              '<span class="mqz-hero__stat-value">' +
              C.escape(stat.value) +
              '</span>' +
              '<span class="mqz-hero__stat-label">' +
              C.escape(stat.label) +
              '</span>' +
              '</div>'
            );
          })
          .join('') +
        '</div>';
    }

    return (
      '<div class="mqz-hero">' +
      (hero.image
        ? '<div class="mqz-hero__media"><img src="' +
          C.escape(hero.image) +
          '" alt="' +
          C.escape(hero.imageAlt || '') +
          '" loading="lazy" decoding="async"></div>'
        : '') +
      (hero.caption ? '<p class="mqz-hero__caption">' + C.escape(hero.caption) + '</p>' : '') +
      stats +
      '</div>'
    );
  }

  function renderMultiSelect(step, ctx) {
    var selected = state[step.stateKey] || [];
    var Card = cardFor(step.cardStyle);

    return (
      '<div class="mqz__grid mqz__grid--' +
      gridClass(step.layout) +
      '" role="group" aria-labelledby="mqz-headline-' +
      C.escape(step.id) +
      '">' +
      (step.options || [])
        .map(function (option) {
          return Card(option, Object.assign({}, ctx, { selected: selected.indexOf(option.id) !== -1 }));
        })
        .join('') +
      '</div>'
    );
  }

  /**
   * The perk a plan gives on the DNA line, phrased for the answer the user
   * actually gave: a discount on the kit, or a free upload.
   */
  function dnaPerkLabel(step, plan) {
    var perk = step.dnaPerk || {};

    if (state.dnaChoice === 'kit' && plan.dnaKitDiscount) {
      var kit = findOption('dna', 'kit');
      var listPrice = (kit && kit.price) || (PRODUCTS.dnaKit && PRODUCTS.dnaKit.price) || 0;
      /* A discount that covers the whole kit is worth saying outright. */
      var free = listPrice && plan.dnaKitDiscount >= listPrice;
      return interpolate(free ? perk.kitFree || perk.kit : perk.kit, {
        amount: plan.dnaKitDiscount
      });
    }

    if (state.dnaChoice === 'upload' && plan.dnaUploadFree) return interpolate(perk.upload || '');
    return '';
  }

  function renderPlanSelect(step, ctx) {
    var media = step.media && step.media.image
      ? '<div class="mqz-plan-media"><img src="' +
        C.escape(step.media.image) +
        '" alt="' +
        C.escape(step.media.imageAlt || '') +
        '" decoding="async"></div>'
      : '';

    return (
      media +
      '<div class="mqz__grid mqz__grid--' +
      gridClass(step.layout) +
      '" role="radiogroup" aria-labelledby="mqz-headline-' +
      C.escape(step.id) +
      '">' +
      (step.options || [])
        .map(function (plan) {
          return C.PricingCard(
            plan,
            Object.assign({}, ctx, {
              selected: state.membershipPlan === plan.id,
              perk: dnaPerkLabel(step, plan)
            })
          );
        })
        .join('') +
      '</div>'
    );
  }

  function renderSummary(step, ctx) {
    var summary = computeSummary();
    summary.extras = renderSummaryExtras(step, summary, ctx);
    return C.OrderSummary(step, summary, ctx);
  }

  /** Upsell (no supplements) or plan upgrade (monthly selected). Never both. */
  function renderSummaryExtras(step, summary, ctx) {
    var upsell = step.upsell || {};
    var upgrade = step.upgrade || {};

    if (upsell.enabled && !summary.planConfig && !state.upsellDismissed) {
      var offered = findPlan(upsell.planId);
      if (!offered) return '';

      return (
        '<div class="mqz__extras">' +
        C.UpsellCard(
          Object.assign({}, upsell, {
            variant: 'add',
            price: offered.priceDueToday,
            compareAtPrice: offered.priceCompareAt || null
          }),
          ctx
        ) +
        '</div>'
      );
    }

    if (upgrade.enabled && summary.planConfig && summary.planConfig.id === upgrade.fromPlanId && !state.upgradeDismissed) {
      var target = findPlan(upgrade.toPlanId);
      if (!target) return '';

      return (
        '<div class="mqz__extras">' +
        C.UpsellCard(
          Object.assign({}, upgrade, {
            variant: 'upgrade',
            image: upsell.image || null,
            imageAlt: upsell.imageAlt || '',
            optionLabel: target.name,
            price: target.priceDueToday,
            compareAtPrice: target.priceCompareAt || null,
            /* The offer sits in a compact row on the summary, so the target
               plan's full benefit list is deliberately left out. */
            secondaryCta: upgrade.dismissCta
          }),
          ctx
        ) +
        '</div>'
      );
    }

    return '';
  }

  /** The summary's CTA lives in the sticky bar, so it only needs the error slot. */
  function renderSummaryFooter() {
    return '<div class="mqz__actions"><p class="mqz__error" data-mqz-error hidden role="alert"></p></div>';
  }

  function renderSticky(step) {
    if (step.autoAdvance) {
      stickyEl.innerHTML = '';
      root.style.setProperty('--mqz-sticky-h', '0px');
      return;
    }

    var isSummary = step.type === 'summary';
    var summary = computeSummary();
    var complete = isStepComplete(step);
    var showBar = isSummary || complete;

    stickyEl.innerHTML = C.StickyContinueButton({
      visible: showBar,
      enabled: isSummary ? true : complete,
      ctaOnly: !isSummary && complete,
      total: isSummary ? formatMoney(summary.dueToday) : summary.dueToday > 0 ? formatMoney(summary.dueToday) : '',
      /* Only the summary names the figure — earlier steps show a running total
         that is not yet what will be charged. */
      totalLabel: isSummary ? LABELS.dueToday || '' : '',
      /* When the plan is charged after the results, the total alone would
         under-read the plan, so the bar says when the rest is taken. */
      note: isSummary ? summary.dueLater : '',
      label: isSummary ? step.cta || 'Checkout' : step.cta || LABELS.continue || 'Continue'
    });

    /* Keep the last card clear of the fixed bar when it is visible. */
    requestAnimationFrame(function () {
      var bar = stickyEl.querySelector('[data-mqz-sticky]');
      root.style.setProperty('--mqz-sticky-h', showBar && bar ? bar.offsetHeight + 'px' : '0px');
    });
  }

  /* ======================================================================
     Events
     ====================================================================== */

  function bindStage() {
    var step = currentStep();

    stageEl.querySelectorAll('[data-mqz-option]').forEach(function (card) {
      card.addEventListener('click', function () {
        selectOption(step, card.dataset.mqzOption);
      });
      card.addEventListener('keydown', function (event) {
        handleCardKeydown(event, card, step);
      });
    });

    var next = stageEl.querySelector('[data-mqz-next]');
    if (next) next.addEventListener('click', goNext);

    var upsell = stageEl.querySelector('[data-mqz-upsell]');
    if (upsell) upsell.addEventListener('click', applyUpsell);

    var upsellDismiss = stageEl.querySelector('[data-mqz-upsell-dismiss]');
    if (upsellDismiss) upsellDismiss.addEventListener('click', dismissUpsell);

    var upgrade = stageEl.querySelector('[data-mqz-upgrade]');
    if (upgrade) upgrade.addEventListener('click', applyUpgrade);

    var upgradeDismiss = stageEl.querySelector('[data-mqz-upgrade-dismiss]');
    if (upgradeDismiss) upgradeDismiss.addEventListener('click', dismissUpgrade);
  }

  /** Roving arrow-key navigation within a card group, plus Space/Enter select. */
  function handleCardKeydown(event, card, step) {
    var cards = Array.prototype.slice.call(stageEl.querySelectorAll('[data-mqz-option]'));
    var index = cards.indexOf(card);
    var isMulti = step.type === 'multi-select';
    var target = -1;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      target = (index + 1) % cards.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      target = (index - 1 + cards.length) % cards.length;
    } else if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      selectOption(step, card.dataset.mqzOption);
      return;
    }

    if (target < 0) return;
    event.preventDefault();
    cards[target].focus();
    /* Radio groups select on arrow; checkbox groups only move focus. */
    if (!isMulti) selectOption(step, cards[target].dataset.mqzOption, { keepFocus: true });
  }

  function selectOption(step, optionId, options) {
    var opts = options || {};
    clearError();

    if (step.type === 'multi-select') {
      var list = state[step.stateKey] || [];
      var at = list.indexOf(optionId);
      state[step.stateKey] = at === -1 ? list.concat([optionId]) : list.filter(function (id) {
        return id !== optionId;
      });
    } else {
      var previous = state[step.stateKey];
      state[step.stateKey] = optionId;

      /* Changing the supplements answer invalidates a plan chosen earlier,
         and any upsell that was added in its place. */
      if (step.id === 'supplements' && previous !== optionId) {
        if (optionId === 'no') state.membershipPlan = null;
        state.upsells = [];
        state.upgradeDismissed = false;
      }
      if (step.type === 'plan-select') state.upgradeDismissed = false;
    }

    persist();

    if (step.autoAdvance) {
      repaintSelection(step, optionId);
      window.setTimeout(function () {
        goNext();
      }, 320);
      return;
    }

    repaintSelection(step, opts.keepFocus ? optionId : null);
  }

  /**
   * Repaints selection in place rather than re-rendering the step, so focus,
   * scroll position and the card animations are preserved.
   */
  function repaintSelection(step, focusId) {
    var selected = state[step.stateKey];

    stageEl.querySelectorAll('[data-mqz-option]').forEach(function (card) {
      var id = card.dataset.mqzOption;
      var isSelected =
        step.type === 'multi-select' ? (selected || []).indexOf(id) !== -1 : selected === id;

      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });

    if (focusId) {
      var focusEl = stageEl.querySelector('[data-mqz-option="' + focusId + '"]');
      if (focusEl) focusEl.focus();
    }

    renderProgress(step);
    renderSticky(step);
  }

  /* ======================================================================
     Upsell / upgrade
     ====================================================================== */

  function applyUpsell() {
    var step = findStepByType('summary');
    var upsell = (step && step.upsell) || {};
    if (!upsell.planId) return;

    state.upsells = [{ type: 'plan', planId: upsell.planId, source: 'summary-upsell' }];
    persist();
    render();
  }

  function dismissUpsell() {
    state.upsellDismissed = true;
    persist();
    render();
  }

  function applyUpgrade() {
    var step = findStepByType('summary');
    var upgrade = (step && step.upgrade) || {};
    if (!upgrade.toPlanId) return;

    /* Update the selection in place — the user stays on the summary. */
    state.membershipPlan = upgrade.toPlanId;
    state.upsells = [];
    persist();
    render();
  }

  function dismissUpgrade() {
    state.upgradeDismissed = true;
    persist();
    render();
  }

  /* ======================================================================
     Cart + checkout
     ====================================================================== */

  /**
   * Validates, builds the line items, writes cart attributes, applies any
   * plan discount code, then hands off to /checkout. Mirrors the sequence
   * already used by membership-buy-box and build-your-plan.
   */
  function handleCheckout() {
    var summary = computeSummary();

    if (!state.dnaChoice) {
      showError(LABELS.errorSelectOne || 'Please choose a DNA option.');
      goTo('dna');
      return;
    }

    var items = buildCartItems(summary);
    if (!items.length) {
      showError(LABELS.errorGeneric || 'Something went wrong. Please try again.');
      return;
    }

    setCheckoutLoading(true);
    clearError();

    clearCart()
      .then(function () {
        return addToCart(items);
      })
      .then(function () {
        return updateCartAttributes(summary);
      })
      .then(function () {
        return applyDiscount(summary);
      })
      .then(function () {
        clearPersisted();
        window.location.href = checkoutUrl();
      })
      .catch(function (err) {
        setCheckoutLoading(false);
        showError((err && err.message) || LABELS.errorGeneric || 'Something went wrong. Please try again.');
      });
  }

  function buildCartItems(summary) {
    var items = [];
    var plan = summary.planConfig;
    var goalTitles = summary.goals.join(', ');

    /* --- DNA ----------------------------------------------------------- */
    var dnaProduct = state.dnaChoice === 'kit' ? PRODUCTS.dnaKit : PRODUCTS.dnaUpload;
    if (dnaProduct && dnaProduct.variantId) {
      items.push({
        id: Number(dnaProduct.variantId),
        quantity: 1,
        properties: { _dna_path: state.dnaChoice }
      });
    }

    /* --- Supplement plan ----------------------------------------------- */
    if (plan && plan.variantId) {
      var planItem = {
        id: Number(plan.variantId),
        quantity: 1,
        properties: {
          _plan: plan.id,
          _dna_path: state.dnaChoice
        }
      };

      /* Goals travel on the membership line so fulfilment can formulate
         from them. Kept out of the URL entirely. */
      if (goalTitles) planItem.properties.Goals = goalTitles;
      if (state.tier === 'pro') planItem.properties._tier = 'pro';
      if (plan.sellingPlanId) planItem.selling_plan = Number(plan.sellingPlanId);

      items.push(planItem);
    }

    /* --- Free welcome kit ---------------------------------------------- */
    if (planIncludesWelcomeKit(plan) && PRODUCTS.welcomeKit && PRODUCTS.welcomeKit.variantId) {
      items.push({
        id: Number(PRODUCTS.welcomeKit.variantId),
        quantity: 1,
        properties: { _gift: 'welcome-kit' }
      });
    }

    /* --- Free genetics expert consultation ----------------------------- */
    if (planIncludesConsultation(plan) && PRODUCTS.consultation && PRODUCTS.consultation.variantId) {
      items.push({
        id: Number(PRODUCTS.consultation.variantId),
        quantity: 1,
        properties: { _gift: 'genetics-consultation' }
      });
    }

    return items;
  }

  function clearCart() {
    if (!SETTINGS.clearCartBeforeAdd) return Promise.resolve();
    return fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('Could not reset your cart.');
    });
  }

  function addToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      if (res.ok) return res.json();
      return res
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          throw new Error(data.description || data.message || 'Could not add items to your cart.');
        });
    });
  }

  function updateCartAttributes(summary) {
    var attributes = {
      'DNA Path': summary.dna ? summary.dna.title : '',
      Goals: summary.goals.join(', '),
      Supplements: summary.planConfig ? 'Personalized Formula' : 'None',
      Plan: summary.planConfig ? summary.planConfig.name : 'DNA only',
      'Quiz Source': 'guided-quiz'
    };

    /* Merge ad-attribution params so they persist onto the order, matching
       the other Myoform funnels. */
    if (window.MyoAttrib && typeof window.MyoAttrib.getCartAttributes === 'function') {
      var attribution = window.MyoAttrib.getCartAttributes();
      Object.keys(attribution).forEach(function (key) {
        attributes[key] = attribution[key];
      });
    }

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    });
  }

  function applyDiscount(summary) {
    var code = summary.planConfig && summary.planConfig.discountCode;
    if (!code) return Promise.resolve();

    /* Warms the discount cookie so it is already applied at checkout.
       A failure here must not block the purchase. */
    return fetch('/discount/' + encodeURIComponent(code), { method: 'GET' }).catch(function () {
      return null;
    });
  }

  function checkoutUrl() {
    var url = SETTINGS.checkoutUrl || '/checkout';
    if (window.MyoAttrib && typeof window.MyoAttrib.appendToUrl === 'function') {
      url = window.MyoAttrib.appendToUrl(url);
    }
    return url;
  }

  function setCheckoutLoading(loading) {
    root.querySelectorAll('[data-mqz-sticky-cta]').forEach(function (btn) {
      btn.classList.toggle('is-loading', loading);
      btn.disabled = loading;
      btn.setAttribute('aria-busy', loading ? 'true' : 'false');

      var label = btn.querySelector('[data-mqz-sticky-label]');
      if (!label) return;

      if (loading) {
        label.dataset.mqzPrevLabel = label.textContent;
        label.textContent = LABELS.processing || 'Processing…';
      } else if (label.dataset.mqzPrevLabel) {
        label.textContent = label.dataset.mqzPrevLabel;
      }
    });
  }

  /* ======================================================================
     Viewport fit
     ====================================================================== */

  /**
   * The lock sizes the flow to the viewport less the sticky header, and that
   * header is 76px on a laptop but 91px on a tablet — the stylesheet can only
   * guess at one number. Measuring it is worth the few lines: the guess was
   * short, so every step sat that much too low and the last card ended up
   * under the sticky bar.
   */
  function measureHeaderOffset() {
    if (document.body.classList.contains('mqz-page-scrolls')) return;

    var main = document.getElementById('MainContent');
    var top = main ? main.getBoundingClientRect().top + window.pageYOffset : 0;
    root.style.setProperty('--mqz-header-offset', Math.max(0, Math.round(top)) + 'px');
  }

  var headerMeasureTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(headerMeasureTimer);
    headerMeasureTimer = setTimeout(measureHeaderOffset, 120);
  });

  /* The header sets its own height from a webfont, so its first measurement is
     taken before that font arrives. */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(measureHeaderOffset);
  }

  /* ======================================================================
     Delegated listeners for the persistent chrome
     ====================================================================== */

  progressEl.addEventListener('click', function (event) {
    if (event.target.closest('[data-mqz-back]')) goBack();
  });

  stickyEl.addEventListener('click', function (event) {
    if (!event.target.closest('[data-mqz-sticky-cta]')) return;
    if (currentStep().type === 'summary') {
      handleCheckout();
    } else {
      goNext();
    }
  });

  /* ======================================================================
     Boot
     ====================================================================== */

  measureHeaderOffset();
  render();
})();
